import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import pointsAPI from '../services/pointsApi';
 
import { Bell } from 'lucide-react';
import { InterviewRecordAPI } from '../services/interviewRecordApi';

 

const Container = styled.div`
  min-height: 100vh;
  background: #0f172a;
  color: #e2e8f0;
  padding: 24px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
`;

const Actions = styled.div`
  margin-left: auto;
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  background: #1f2937;
  color: #e5e7eb;
  border: 1px solid #374151;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s ease;
  &:hover { background: #262c3a; }
`;

const List = styled.div`
  background: #0b1220;
  border: 1px solid #1f2937;
  border-radius: 12px;
  overflow: hidden;
`;

const Item = styled.div`
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid #1f2937;
  align-items: flex-start;
  &:last-child { border-bottom: none; }
`;

const IconWrap = styled.div`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #111827;
  border: 1px solid #1f2937;
`;

const Content = styled.div`
  flex: 1;
`;

const Line1 = styled.div`
  font-size: 14px;
  color: #e5e7eb;
`;

const Line2 = styled.div`
  font-size: 12px;
  color: #94a3b8;
  margin-top: 2px;
`;

const TimeText = styled.div`
  font-size: 12px;
  color: #64748b;
`;

function formatTime(ts) {
  try {
    // 使用北京时间（UTC+8）格式化时间
    const d = new Date(ts);
    // 转换为北京时间（UTC+8）
    const beijingTime = new Date(d.getTime() + (8 * 60 - d.getTimezoneOffset()) * 60 * 1000);
    const y = beijingTime.getUTCFullYear();
    const m = beijingTime.getUTCMonth() + 1;
    const day = beijingTime.getUTCDate();
    const hh = String(beijingTime.getUTCHours()).padStart(2, '0');
    const mm = String(beijingTime.getUTCMinutes()).padStart(2, '0');
    const ss = String(beijingTime.getUTCSeconds()).padStart(2, '0');
    return `${y}/${m}/${day} ${hh}:${mm}:${ss}`;
  } catch { return String(ts); }
}

// 将reason中的英文单词转换为中文
function translateReason(reason) {
  if (!reason) return reason;
  // 将常见的英文订阅周期转换为中文
  let translated = reason
    .replace(/订阅month充值/gi, '订阅月充值')
    .replace(/订阅week充值/gi, '订阅周充值')
    .replace(/订阅quarter充值/gi, '订阅季度充值')
    .replace(/month/gi, '月')
    .replace(/week/gi, '周')
    .replace(/quarter/gi, '季度');
  return translated;
}

// 删除字符串末尾的所有标点符号（。、！、？等）
function removePunctuation(text) {
  return text.replace(/[。，、！？；：]+$/g, '');
}

function buildPointsMessage(entry) {
  const change = entry.change;
  const abs = Math.abs(change);
  let reason = entry.reason || '';
  // 先转换中英文混杂的内容
  reason = translateReason(reason);
  
  if (reason === 'recharge') return removePunctuation(`充值成功，获得 ${abs} 积分`);
  if (reason === 'referral_success') return removePunctuation(`邀请成功奖励 +${abs} 积分`);
  if (reason === 'initial_bonus') return removePunctuation(`新用户赠送 +${abs} 积分`);
  if (reason === 'subscription_expired_clear' && change < 0) {
    return '会员已到期，本期积分已清零';
  }
  if (change < 0) {
    if (reason === 'resume_diagnosis') {
      return `简历诊断-功能使用扣减 ${abs} 积分`;
    }
    if (reason === 'interview_prep') {
      return `面试押题-功能使用扣减 ${abs} 积分`;
    }
    if (reason === 'mock_interview') {
      return `模拟面试-按时长扣减 ${abs} 积分`;
    }
    if (reason === 'meeting_assistant') {
      return `会议助手-按时长扣减 ${abs} 积分`;
    }
    if (reason === 'resume_pdf_export') {
      return `简历生成-PDF导出扣减 ${abs} 积分`;
    }
    return removePunctuation(`功能使用扣减 ${abs} 积分（${reason}）`);
  }
  if (change > 0) {
    // 处理"订阅month充值"等中英文混杂的情况
    if (reason.includes('订阅') && reason.includes('充值')) {
      return removePunctuation(`积分变更 +${abs}（${reason}）`);
    }
    return removePunctuation(`积分变更 +${abs}（${reason}）`);
  }
  return removePunctuation(`积分记录（${reason}）`);
}

const NotificationsPage = () => {
  const { user } = useAuth();
  const userId = user?.user_id || null;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const lastSeenKey = useMemo(() => `notifications_last_seen_${userId || 'guest'}`, [userId]);

  useEffect(() => {
    const load = async () => {
      if (!userId) return;
      setLoading(true);
      try {
        const [pointsRes, recordsRes] = await Promise.allSettled([
          pointsAPI.getHistory(userId),
          InterviewRecordAPI.fetchInterviewRecordsRemote(userId).then(r => r.success ? (r.data || []) : [])
        ]);

        const list = [];

        if (pointsRes.status === 'fulfilled' && pointsRes.value?.entries) {
          const entries = pointsRes.value.entries || [];
          let running = pointsRes.value.balance ?? 0;
          for (const e of entries) {
            const balanceAfter = running;
            list.push({
              id: `points_${e.id}`,
              type: 'points',
              title: buildPointsMessage(e),
              desc: `当前余额 ${balanceAfter} 积分`,
              time: e.created_at
            });
            running -= e.change;
          }
        }


        // 不再添加“邀请统计更新”类型的聚合通知

        // 去重与只保留最近 10 条
        const seenIds = new Set();
        const seenKey = new Set();
        const deduped = [];
        for (const n of list) {
          const key = `${n.title}|${n.time}`;
          if (seenIds.has(n.id) || seenKey.has(key)) continue;
          seenIds.add(n.id);
          seenKey.add(key);
          deduped.push(n);
        }

        deduped.sort((a, b) => new Date(b.time) - new Date(a.time));
        setItems(deduped.slice(0, 10));
      } catch (e) {
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId]);

  const markAllRead = () => {
    try {
      // 使用当前所有通知中的最大时间戳，而不是当前时间
      // 这样可以确保所有当前显示的通知都被标记为已读
      if (items.length > 0) {
        const maxTime = Math.max(...items.map(n => {
          const time = new Date(n.time).getTime();
          return isNaN(time) ? 0 : time;
        }));
        if (maxTime > 0) {
          localStorage.setItem(lastSeenKey, maxTime.toString());
        } else {
          // 如果没有有效的时间戳，使用当前时间
          localStorage.setItem(lastSeenKey, Date.now().toString());
        }
      } else {
        // 如果没有通知，使用当前时间
        localStorage.setItem(lastSeenKey, Date.now().toString());
      }
    } catch {}
  };

  return (
    <Container>
      <Header>
        <Bell size={20} />
        <Title>通知中心</Title>
        <Actions>
          <Button onClick={markAllRead}>全部标为已读</Button>
        </Actions>
      </Header>

      <List>
        {loading && (
          <Item>
            <IconWrap><Bell size={16} /></IconWrap>
            <Content>
              <Line1>加载中...</Line1>
              <Line2>正在获取您的通知</Line2>
            </Content>
          </Item>
        )}
        {!loading && items.length === 0 && (
          <Item>
            <IconWrap><Bell size={16} /></IconWrap>
            <Content>
              <Line1>暂无通知</Line1>
              <Line2>系统会在您有新的订阅、充值、功能使用或积分变动时提醒</Line2>
            </Content>
          </Item>
        )}
        {items.map(n => (
          <Item key={n.id}>
            <IconWrap><Bell size={16} /></IconWrap>
            <Content>
              <Line1>{n.title}</Line1>
              {n.desc && <Line2>{n.desc}</Line2>}
            </Content>
            <TimeText>{formatTime(n.time)}</TimeText>
          </Item>
        ))}
      </List>
    </Container>
  );
};

export default NotificationsPage;
